import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Iterable } from 'immutable';
import { SingleSelect } from '@els/els-react--select';
import usePostActionFocus from '@Hooks/usePostActionFocus';

import './ResultsPerPage.scss';

function ResultsPerPage({ resultsPerPage, dropdownValues, setResultsPerPage }) {
  const CONTROL_ID = 'results-per-page';
  const isCurrentValue = (value) => value === resultsPerPage;
  const [selectedValue, setSelectedValue] = useState(resultsPerPage);
  const [options, setOptions] = useState([]);
  const pageSelectorRef = useRef(null);
  const { ref: dropdownWrapperRef, markActive } = usePostActionFocus({
    id: CONTROL_ID,
    // Only attempt to restore focus after the external prop changes (post-reload).
    // Relying on selectedValue caused a race with SingleSelect's internal focus.
    dependentKeys: [resultsPerPage],
    forwardedRef: pageSelectorRef, // pass inputRef so hook can focus the internal input
    avoidIfActiveSelector: '.bar-chart-checkboxes-dialog__go-to-page',
  });

  useEffect(() => {
    const optionValues = dropdownValues && dropdownValues.length > 0 ? dropdownValues.map((item) => item.value) : [];
    setOptions(optionValues);

    // Handle case where resultsPerPage doesn't match any dropdown option RX-19499
    if (resultsPerPage && optionValues.length > 0) {
      const isValidOption = optionValues.includes(resultsPerPage);

      if (isValidOption) {
        setSelectedValue(resultsPerPage);
      } else {
        setSelectedValue(optionValues[0]);
      }
    }
  }, [dropdownValues, resultsPerPage]);

  const selectHandler = (value) => {
    const isNewValue = !isCurrentValue(value);
    // Only mark this control as active if the selection actually changes the external value
    if (isNewValue) {
      markActive();
    }

    // update local selection immediately so the local state and focus effect run
    setSelectedValue(value);
    if (isNewValue) {
      setResultsPerPage(value);
    }
  };

  return (
    <div className="results-per-wrapper clearfix">
      <div className="results-per-label">Results per page</div>
      <div ref={dropdownWrapperRef} className="results-per-dropdown u-els-margin-left-1x u-els-margin-right-1x">
        {options && options.length > 0 && (
          <SingleSelect
            options={options}
            inputRef={pageSelectorRef}
            selectedOptions={selectedValue ? [selectedValue] : [options[0]]}
            blankOptionLabel={selectedValue ? `${selectedValue}` : 'Select...'}
            label="Select an option"
            required
            onSelect={(l) => {
              selectHandler(l);
            }}
            size="small"
            shouldHideLabel
            className="u-docs-position-relative u-docs-z-index"
          />
        )}
      </div>
    </div>
  );
}

ResultsPerPage.propTypes = {
  resultsPerPage: PropTypes.number,
  dropdownValues: PropTypes.oneOfType([PropTypes.instanceOf(Iterable), PropTypes.array]).isRequired,
  setResultsPerPage: PropTypes.func.isRequired,
};

export default ResultsPerPage;
